<template>
    <wrapper-features-templates-one-column
    buttonFullWidth
    :hideWhiteBoxOnLoad="showLoader || (!getProductsList.length && !showLoader && !applyErrorState)"
    :showLoader="showLoader"
    :applyErrorState="applyErrorState"
	:showFooter="!showLoader && !applyErrorState && getMobileAppState && getProductsList.length"
	:footerButtonsParams="footerButtonsData"
    @backOrCloseClicked="emit('backOrCloseClicked')">
        <transition-group name="common-enter-animation" mode="out-in" appear>
            <card-product-selector-wrapper
            v-if="!showLoader && !applyErrorState && getProductsList.length"
            :applyMobileHeaderProductSlider="getMobileAppState"
            :creditProduct="getMobileAppState"
            :productsList="parsedProductList"/>
        </transition-group>
        <!-- Empty State -->
        <template #empty-state-slot>
            <empty-state-generic
            v-if="!showLoader && !applyErrorState && !getProductsList.length"
            illustration="monocrome/empty-state-credit"
            :title="t('credits.creditSolutions.productsList.emptyStateGeneric.title')"
            :description="t('credits.creditSolutions.productsList.emptyStateGeneric.description')"/>
        </template>
    </wrapper-features-templates-one-column>
    <!-- Error Feedback -->
    <toast-generic
    v-if="showFeedbackToast"
    :toastType="feedbackToastData.toastType"
    :feedbackText="feedbackToastData.text"
    :buttonsParams="feedbackToastData.buttons"
    @hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import WrapperFeaturesTemplatesOneColumn from '@/components/wrappers/featuresTemplates/OneColumn'
import CardProductSelectorWrapper from '@/components/generic/cards/productSelector/SelectorsWrapper'
import ToastGeneric from '@/components/generic/toasts/Generic'
import EmptyStateGeneric from '@/components/generic/emptyStates/Generic'

/* Assets and Helpers */
import { amountFormat } from '@/assets/scripts/amounts'
import { navigateByName } from '@/router/helpers/navigation'
import { routeList } from '@/router/helpers/routes'
import { errorObjectConstructor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'

/* Vue */
import { ref, computed } from 'vue'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])

/** Props
@dashboardCardInfo {Object} - info received to select product to subscribe
*/
const props = defineProps({
    dashboardCardInfo: Object
})

/* mapActions & mapGetters */
const {	requestproductsList, validateClientNif } = mapActions(moduleMapper.CREDITS_CREDITS_SOLUTIONS)
const { getProductsList } = mapGetters(moduleMapper.CREDITS_CREDITS_SOLUTIONS)
const { getMobileAppState, getClientType } = mapGetters()

/* Data */
const showLoader = ref(false)
const applyErrorState = ref(false)
const selectedProduct = ref(false)
const mobileSelectedIndex = ref(0)

/* Footer Buttons Data */
const footerButtonsData = computed(() => {
	return [{
		label: t('credits.creditSolutions.productsList.buttonElevatedText'),
		icon: 'arrow-right-full',
		buttonType: 'primary',
		iconPosition: 'right',
		callbackFunction: () => {
            navigateToSubscriveCredit(getProductsList.value[mobileSelectedIndex.value])
        }
	}]
})

const navigateToSubscriveCredit = async ($event) => {
    try {
        if (getClientType.value === 'company' && getProductsList.value.find(obj => obj.id === $event.id).subProduct.code === 'ADIANTAJÁE') {
            showLoader.value = true

            await validateClientNif()
            showLoader.value = false
        }

        selectedProduct.value = getProductsList.value.find(obj => obj.id === $event.id)
        navigateByName(routeList.CREDIT_CREDIT_SOLUTIONS_SUBSCRIBE_PRODUCT_NAME, {
            selectedProduct: { ...selectedProduct.value, accountId: props.dashboardCardInfo?.accountId }
        })
    } catch (error) {
        if (error.toString() === 'Cancel') return
        errorHandler(error.response.data, () => navigateToSubscriveCredit($event))
    }
}

const parsedProductList = computed(() => {
    if (!getProductsList.value) return

	const productList = []

	getProductsList.value.forEach(product => {
        productList.push({
            id: product.id,
            currency: product.currency.name,
            title: product.name,
            description: product.description,
            infoSheetUrl: product.additionalInfoSheetUrl,
            infoSheetUrlBtnText: t('credits.creditSolutions.productsList.buttonRaisedText'),
            infoSheetCustomSuccessMessage: t('credits.creditSolutions.productsList.downloadInfoSheetSuccessMessage'),
            secondBtnText: t('credits.creditSolutions.productsList.buttonElevatedText'),
            secondBtnClicked: navigateToSubscriveCredit,
            textBlocks: [
                /* Duration */
                {
                    label: t('credits.creditSolutions.productsList.durationTextBlockLabel'),
                    content: product.durationDescription
                },
                /* Minimum Amount */
                {
                    label: t('credits.creditSolutions.productsList.minimumAmountTextBlockLabel'),
                    content: product.minimumAmount && product.currency.name !== ''
                    ? amountFormat(product.minimumAmount, product.currency.name)
                    : '-'
                },
                /* Maximum Amount */
                {
                    label: t('credits.creditSolutions.productsList.maximumAmountTextBlockLabel'),
                    content: product.maximumAmount && product.currency.name !== ''
                    ? amountFormat(product.maximumAmount, product.currency.name)
                    : '-'
                }
            ]
        })
	})

	return productList
})

/*************************************************
*                                                *
*             Products List Request              *
*                                                *
*************************************************/
const productsList = async () => {
    showLoader.value = true
    applyErrorState.value = false

    try {
        await requestproductsList()
        if (props.dashboardCardInfo) {
            navigateToSubscriveCredit({ id: props.dashboardCardInfo.dashboardCardAdditionalData.creditProductMetaDataId })
            return
        }
        showLoader.value = false
    } catch (error) {
        if (error.toString() === 'Cancel') return
        applyErrorState.value = true
        errorHandler(error.response.data, productsList)
    }
}

/*************************************************
*                                                *
*                 Error Feedback                 *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)

const feedbackToastData = ref(emptyObjectConstructor())

/*************************************************
*                                                *
*                 Error Handlers                 *
*                                                *
*************************************************/
const errorHandler = (data, retryFn) => {
    showLoader.value = false

    feedbackToastData.value = errorObjectConstructor(data, retryFn)
    showFeedbackToast.value = true
}

productsList()
</script>

<style lang="scss" scoped>
:deep(.slider-content-wrapper) .slider-wrapper {
    padding: 16px 16px 0 16px;
}

.subscribe-content {
    @include commonEnteringAnimation;
    width: 100%;

    display: flex;
    flex-direction: column;
    gap: 16px;
}
</style>
